import { getUnitDisplayAddress } from "../lib/unitAddressUtils";

const SCHEDULE_E_ALLOWED = new Set([
  "Advertising",
  "Insurance",
  "Legal/Professional",
  "Management Fees",
  "Mortgage Interest",
  "Repairs",
  "Supplies",
  "Taxes",
  "Utilities",
  "Other Expenses",
]);

function toJsDate(value) {
  if (value == null) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole months (inclusive) where lease intersects [Jan 1, Dec 31] of taxYear. */
export function monthsOverlappingTaxYear(lease, taxYear) {
  const yearStart = new Date(taxYear, 0, 1);
  const yearEnd = new Date(taxYear, 11, 31, 23, 59, 59, 999);

  let leaseStart = toJsDate(lease.startDate);
  let leaseEnd = toJsDate(lease.endDate);

  const status = String(lease.status || "").toLowerCase();
  if (!leaseStart) leaseStart = yearStart;
  if (!leaseEnd) {
    if (status === "active" || status === "month_to_month") leaseEnd = yearEnd;
    else return 0;
  }

  const start = leaseStart < yearStart ? yearStart : leaseStart;
  const end = leaseEnd > yearEnd ? yearEnd : leaseEnd;
  if (start > end) return 0;

  let n = 0;
  const d = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (d <= endMonth) {
    n += 1;
    d.setMonth(d.getMonth() + 1);
  }
  return Math.min(12, n);
}

function expenseInTaxYear(expense, taxYear) {
  const d = toJsDate(expense.date);
  if (!d) return true;
  return d.getFullYear() === taxYear;
}

/** Map Expenses page / Firestore labels to Tax Prep wizard Schedule E values */
const CATEGORY_ALIASES = {
  "Legal & Professional Fees": "Legal/Professional",
  "Legal & Professional": "Legal/Professional",
  "Auto & Travel": "Other Expenses",
  "Cleaning & Maintenance": "Repairs",
};

function normalizeCategory(raw) {
  const c = String(raw || "").trim();
  if (SCHEDULE_E_ALLOWED.has(c)) return c;
  const mapped = CATEGORY_ALIASES[c];
  if (mapped && SCHEDULE_E_ALLOWED.has(mapped)) return mapped;
  return "Other Expenses";
}

/**
 * Build Tax Prep form defaults from unified API leases + Firestore expenses (same source as /expenses).
 *
 * @param {Object} params
 * @param {Array} params.leases - From GET /api/leases
 * @param {Array} params.expenses - From getExpensesByOwner().expenses
 * @param {Array} params.units - From unitModel.getByOwner
 * @param {number} params.taxYear - Calendar year (e.g. 2025)
 * @param {{ displayName?: string, email?: string }} [params.userProfile]
 * @returns {{ hasData: boolean, properties: {address: string}[], totalRentCollected: string, expenses: {amount: string, category: string}[], userName: string, userEmail: string }}
 */
export function buildPortfolioTaxPrefill({
  leases = [],
  expenses = [],
  units = [],
  taxYear,
  userProfile = {},
}) {
  const unitById = new Map(units.map((u) => [u.id, u]));

  const addressForUnit = (unitId) => {
    if (!unitId) return "";
    const u = unitById.get(unitId);
    if (!u) return "";
    return (getUnitDisplayAddress(u) || u.unitNumber || u.name || "").trim();
  };

  const addresses = new Set();
  let totalRent = 0;

  for (const lease of leases) {
    const months = monthsOverlappingTaxYear(lease, taxYear);
    if (months <= 0) continue;
    const monthly = Number(lease.rentAmount) || 0;
    totalRent += monthly * months;

    const addr = addressForUnit(lease.unitId);
    if (addr) addresses.add(addr);
  }

  const expenseRows = (expenses || [])
    .filter((e) => expenseInTaxYear(e, taxYear))
    .map((e) => ({
      amount: String(Number(e.amount) || 0),
      category: normalizeCategory(e.category),
    }))
    .filter((e) => parseFloat(e.amount) > 0);

  for (const e of expenses || []) {
    if (!expenseInTaxYear(e, taxYear)) continue;
    const addr = addressForUnit(e.unitId);
    if (addr) addresses.add(addr);
  }

  const properties = [...addresses].sort().map((address) => ({ address }));

  const expenseForm =
    expenseRows.length > 0 ? expenseRows : [{ amount: "", category: "" }];

  const hasData =
    properties.length > 0 || totalRent > 0 || expenseRows.length > 0;

  return {
    hasData,
    properties: properties.length > 0 ? properties : [{ address: "" }],
    totalRentCollected:
      totalRent > 0 ? String(Math.round(totalRent * 100) / 100) : "",
    expenses: expenseForm,
    userName: userProfile.displayName || "",
    userEmail: userProfile.email || "",
  };
}
